import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { PortalDropdown } from "./PortalDropdown";

interface BoardContextMenuProps {
  boardId: string;
  boardName: string;
  onRename: (id: string, newName: string) => void;
  onDelete: (id: string) => void;
}

const BoardContextMenu = ({
  boardId,
  boardName,
  onRename,
  onDelete,
}: BoardContextMenuProps) => {
  const { t } = useTranslation("common");
  const [isOpen, setIsOpen] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [newName, setNewName] = useState(boardName);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isRenaming && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isRenaming]);

  const handleRename = () => {
    if (newName.trim() && newName !== boardName) {
      onRename(boardId, newName.trim());
    }
    setIsRenaming(false);
    setIsOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleRename();
    } else if (e.key === "Escape") {
      setNewName(boardName);
      setIsRenaming(false);
    }
  };

  const menuItems = [
    {
      label: t("actions.rename"),
      onClick: () => setIsRenaming(true),
      icon: (
        <span className="w-4 h-4" style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_88e78b46.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_88e78b46.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} role='img' />
      ),
    },
    {
      label: t("actions.delete"),
      onClick: () => {
        setIsOpen(false); // Close menu first
        onDelete(boardId);
      },
      icon: (
        <span className="w-4 h-4" style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_fd779e52.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_fd779e52.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} role='img' />
      ),
      className: "text-error-600 hover:bg-error-50",
    },
  ];

  return (
    <>
      <button
        ref={triggerRef}
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className="btn-ghost p-2 rounded-full hover:bg-neutral-100 transition-colors"
        aria-label={String(t("actions.openMenu"))}
      >
        <span className="w-4 h-4" style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_540c686a.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_540c686a.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} role='img' />
      </button>

      <PortalDropdown
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false);
          setIsRenaming(false);
        }}
        triggerRef={triggerRef}
        className="w-48"
      >
        {isRenaming ? (
          <div className="p-2">
            <input
              ref={inputRef}
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={handleRename}
              className="w-full px-2 py-1 text-sm border border-neutral-300 rounded focus:outline-none focus:ring-1 focus:ring-accent-primary"
              placeholder={t("boards.boardName") as string}
              autoFocus
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        ) : (
          menuItems.map((item, index) => (
            <button
              key={index}
              onClick={(e) => {
                e.stopPropagation();
                item.onClick();
              }}
              className={`w-full flex items-center space-x-2 px-3 py-2 text-sm text-left hover:bg-neutral-50 transition-colors ${item.className || ""
                }`}
            >
              {item.icon}
              <span>{item.label}</span>
            </button>
          ))
        )}
      </PortalDropdown>
    </>
  );
};

export default BoardContextMenu;
